/**
 * src/lib/supabaseProducts.js
 * Replaces imli-stories' hardcoded lib/products.ts with real Supabase
 * queries. "Sizes" shown on a product are actual "Stock" rows (barcodes) —
 * each carries its own availability (quantity/status), so sold-out sizes
 * can be shown disabled instead of just disappearing.
 */

import { supabase } from "../supabaseClient";

export function formatINR(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN")}`;
}

/**
 * One row per product, with its images and size/stock variants attached.
 * Shape:
 * {
 *   product_id, name, slug, description, gender, category, subcategory, brand,
 *   images: [{ url, position }],
 *   sizes: [{ stock_id, size_label, quantity, status, price, online_price, online_discount, available }]
 *   priceFrom: number,   // lowest effective price across sizes
 *   compareAtFrom: number|null,
 *   anyAvailable: boolean,
 * }
 */
function computeEffectivePrice(stockRow) {
  const base = stockRow.online_price ?? stockRow.price ?? 0;
  const discount = stockRow.online_discount ?? 0;
  return Math.round(base * (1 - discount / 100));
}

// Full expected size range per subcategory — if a product under this
// subcategory has no barcode for one of these sizes, a disabled "sold out"
// placeholder is shown for it on the size selector. This never touches the
// real "Stock" table — it's purely a display-layer fill-in, and these
// dummy entries never affect pricing or availability calculations.
const SUBCATEGORY_SIZE_TEMPLATES = {
  frock: ["6-12 Months", "1-2 years", "2-3 Years", "3-4 Years", "4-5 Years", "5-6 Years", "6-7 Years"],
};

function withDummySizes(realSizes, subcategory) {
  const template = SUBCATEGORY_SIZE_TEMPLATES[(subcategory || "").toLowerCase().trim()];
  if (!template) return realSizes;

  const existingLower = new Set(realSizes.map((s) => (s.sizeLabel || "").toLowerCase().trim()));
  const dummies = template
    .filter((label) => !existingLower.has(label.toLowerCase()))
    .map((label) => ({
      stockId: `dummy-${label.replace(/\s+/g, "-").toLowerCase()}`,
      sizeLabel: label,
      quantity: 0,
      status: "sold_out",
      effectivePrice: 0,
      basePrice: 0,
      available: false,
      isDummy: true,
    }));

  const merged = [...realSizes, ...dummies];
  merged.sort((a, b) => {
    const ai = template.findIndex((t) => t.toLowerCase() === (a.sizeLabel || "").toLowerCase());
    const bi = template.findIndex((t) => t.toLowerCase() === (b.sizeLabel || "").toLowerCase());
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });
  return merged;
}

function shapeProduct(p, images, stockRows) {
  const realSizes = (stockRows || []).map((s) => ({
    stockId: s.stock_id,
    sizeLabel: s.size_label,
    quantity: s.quantity,
    status: s.status,
    effectivePrice: computeEffectivePrice(s),
    basePrice: s.online_price ?? s.price ?? 0,
    available: s.status === "available" && (s.quantity ?? 0) > 0,
    isDummy: false,
  }));

  const sizes = withDummySizes(realSizes, p.subcategory);

  // Price/availability always computed from real sizes only — dummy
  // placeholders (price 0, never available) must never leak into these.
  const availableSizes = realSizes.filter((s) => s.available);
  const priceSource = availableSizes.length > 0 ? availableSizes : realSizes;
  const priceFrom = priceSource.length > 0 ? Math.min(...priceSource.map((s) => s.effectivePrice)) : 0;
  const compareFrom =
    priceSource.length > 0 && priceSource.some((s) => s.basePrice > s.effectivePrice)
      ? Math.min(...priceSource.map((s) => s.basePrice))
      : null;

  return {
    productId: p.product_id,
    name: p.name,
    slug: p.slug,
    description: p.description,
    gender: p.gender,
    category: p.category,
    subcategory: p.subcategory,
    brand: p.brand,
    images: (images || []).sort((a, b) => a.position - b.position).map((i) => i.url),
    sizes,
    priceFrom,
    compareFrom,
    anyAvailable: availableSizes.length > 0,
  };
}

/**
 * Fetches active products matching the given facet filters, each with its
 * images and size/stock rows attached. All filters are optional.
 */
export async function fetchProducts({ gender, categories, subcategories, sizeLabels, limit } = {}) {
  let query = supabase
    .from("products")
    .select(
      `
      product_id, name, slug, description, gender, category, subcategory, brand,
      product_images ( url, position ),
      Stock ( stock_id, size_label, quantity, status, price, online_price, online_discount, is_online_visible )
    `
    )
    .eq("is_online_active", true);

  if (gender) query = query.eq("gender", gender);
  if (categories && categories.length > 0) query = query.in("category", categories);
  if (subcategories && subcategories.length > 0) query = query.in("subcategory", subcategories);
  if (limit) query = query.limit(limit);

  const { data, error } = await query;
  if (error) {
    console.error("fetchProducts error:", error);
    return [];
  }

  let shaped = (data || []).map((p) =>
    shapeProduct(
      p,
      p.product_images,
      (p.Stock || []).filter((s) => s.is_online_visible)
    )
  );

  // Age/size filter applied after shaping, since it's per-variant, not per-product
  if (sizeLabels && sizeLabels.length > 0) {
    shaped = shaped.filter((p) => p.sizes.some((s) => sizeLabels.includes(s.sizeLabel)));
  }

  return shaped;
}

export async function fetchProductBySlug(slug) {
  const { data, error } = await supabase
    .from("products")
    .select(
      `
      product_id, name, slug, description, gender, category, subcategory, brand,
      product_images ( url, position ),
      Stock ( stock_id, size_label, quantity, status, price, online_price, online_discount, is_online_visible )
    `
    )
    .eq("slug", slug)
    .eq("is_online_active", true)
    .single();

  if (error) {
    console.error("fetchProductBySlug error:", error);
    return null;
  }

  return shapeProduct(data, data.product_images, (data.Stock || []).filter((s) => s.is_online_visible));
}

/**
 * Distinct facet values, used to build the filter sidebar dynamically —
 * never hardcoded, since categories/subcategories vary and grow over time.
 */
export async function fetchFacets({ gender } = {}) {
  let query = supabase.from("products").select("category, subcategory").eq("is_online_active", true);
  if (gender) query = query.eq("gender", gender);

  const { data, error } = await query;
  if (error) {
    console.error("fetchFacets error:", error);
    return { categories: [], subcategoriesByCategory: {} };
  }

  const categories = [...new Set((data || []).map((r) => r.category).filter(Boolean))];
  const subcategoriesByCategory = {};
  for (const row of data || []) {
    if (!row.category || !row.subcategory) continue;
    if (!subcategoriesByCategory[row.category]) subcategoriesByCategory[row.category] = new Set();
    subcategoriesByCategory[row.category].add(row.subcategory);
  }
  for (const cat of Object.keys(subcategoriesByCategory)) {
    subcategoriesByCategory[cat] = [...subcategoriesByCategory[cat]];
  }

  return { categories, subcategoriesByCategory };
}

/**
 * Distinct size labels currently in stock — used for the Age filter.
 */
export async function fetchAvailableSizeLabels() {
  const { data, error } = await supabase
    .from("Stock")
    .select("size_label")
    .eq("is_online_visible", true)
    .not("size_label", "is", null);

  if (error) {
    console.error("fetchAvailableSizeLabels error:", error);
    return [];
  }
  return [...new Set(data.map((r) => r.size_label))];
}

/**
 * Homepage "Bestsellers" — products explicitly flagged in the admin panel.
 */
export async function fetchBestsellers(limit = 8) {
  const { data, error } = await supabase
    .from("products")
    .select(
      `
      product_id, name, slug, description, gender, category, subcategory, brand,
      product_images ( url, position ),
      Stock ( stock_id, size_label, quantity, status, price, online_price, online_discount, is_online_visible )
    `
    )
    .eq("is_online_active", true)
    .eq("is_bestseller", true)
    .limit(limit);

  if (error) {
    console.error("fetchBestsellers error:", error);
    return [];
  }
  return (data || []).map((p) => shapeProduct(p, p.product_images, (p.Stock || []).filter((s) => s.is_online_visible)));
}

/**
 * Homepage "Collections" tiles — one tile per (gender, category) combo that
 * actually has live products, each showing that group's first product image.
 * Never hardcoded; reflects whatever's actually tagged in the admin panel.
 */
export async function fetchCollectionTiles(limit = 4) {
  const { data, error } = await supabase
    .from("products")
    .select("gender, category, product_images ( url, position )")
    .eq("is_online_active", true)
    .not("gender", "is", null)
    .not("category", "is", null);

  if (error) {
    console.error("fetchCollectionTiles error:", error);
    return [];
  }

  const seen = new Map();
  for (const row of data || []) {
    const key = `${row.gender}::${row.category}`;
    if (seen.has(key)) continue;
    const sortedImages = (row.product_images || []).sort((a, b) => a.position - b.position);
    if (sortedImages.length === 0) continue; // skip tiles with no image to show
    seen.set(key, {
      gender: row.gender,
      category: row.category,
      label: row.category,
      image: sortedImages[0].url,
    });
  }
  return [...seen.values()].slice(0, limit);
}